'use client';

import { useEffect, useRef, useState } from 'react';
import { observeThemeChange } from '@/lib/themeTokens';
import { AppIcon } from '@/components/ui/AppIcon';
import { drawDesktopRoutes, drawMobileRoutes, MOBILE_MARKERS } from './flowRoutes';
import { FLOW_CATEGORIES, TRADE_SERVICES } from './homeData';

const FLOW_DESCRIPTION =
  'Seller products pass through their product category and AI matching to buyers. Buyer requests travel back through AI and product categories to suppliers. Trade partners support product delivery from suppliers to buyers and payment back to suppliers. Gold represents payment.';

function BuyerColors() {
  return (
    <div className="buyer-colors" aria-hidden="true">
      {FLOW_CATEGORIES.map((category) => (
        <b key={category.name} />
      ))}
    </div>
  );
}

/** Hero diagram: qualified suppliers → products → AI matching → qualified buyers, with trade partners. */
export function GlobalFlow() {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<SVGSVGElement>(null);
  const routesRef = useRef<SVGGElement>(null);
  const mobileRef = useRef<SVGSVGElement>(null);
  const mobileLayerRef = useRef<SVGGElement>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const art = artRef.current;
    const routes = routesRef.current;
    const mobile = mobileRef.current;
    const layer = mobileLayerRef.current;
    if (!root || !stage || !art || !routes || !mobile || !layer) return undefined;
    const desktop = window.matchMedia('(min-width: 601px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;

    const syncMotion = () => {
      [art, mobile].forEach((svg) =>
        reduced.matches ? svg.pauseAnimations() : svg.unpauseAnimations()
      );
    };
    const draw = () => {
      routes.replaceChildren();
      layer.replaceChildren();
      if (desktop.matches) drawDesktopRoutes(root, art, routes, FLOW_CATEGORIES);
      else drawMobileRoutes(root, stage, mobile, layer, FLOW_CATEGORIES);
      syncMotion();
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(draw);
    };
    const observer = new ResizeObserver(schedule);
    [stage, ...Array.from(root.querySelectorAll('.products, .product'))].forEach((node) =>
      observer.observe(node)
    );
    desktop.addEventListener('change', schedule);
    reduced.addEventListener('change', syncMotion);
    // Route colours are resolved palette tokens: redraw when the theme changes.
    const stopThemeWatch = observeThemeChange(schedule);
    draw();
    return () => {
      cancelAnimationFrame(frame);
      stopThemeWatch();
      observer.disconnect();
      desktop.removeEventListener('change', schedule);
      reduced.removeEventListener('change', syncMotion);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      id="cid-global-flow"
      className="cid-flow"
      role="group"
      aria-label="CanIDeal global product matching concept"
      aria-describedby="cid-flow-description"
      data-selected={selected ?? undefined}
    >
      <p id="cid-flow-description" hidden>
        {FLOW_DESCRIPTION}
      </p>
      <div ref={stageRef} className="flow-stage">
        <svg
          ref={mobileRef}
          className="cid-mobile-flow"
          role="img"
          aria-labelledby="cid-mobile-flow-title cid-mobile-flow-description"
        >
          <title id="cid-mobile-flow-title">Two-way matching, delivery and payment</title>
          <desc id="cid-mobile-flow-description">{FLOW_DESCRIPTION}</desc>
          <defs>
            {MOBILE_MARKERS.map((marker) => (
              <marker
                key={marker.id}
                id={marker.id}
                viewBox="0 0 6 6"
                refX={5}
                refY={3}
                markerWidth={5}
                markerHeight={5}
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0 0 L6 3 L0 6 Z" style={{ fill: `var(${marker.token})` }} />
              </marker>
            ))}
          </defs>
          <g ref={mobileLayerRef} />
        </svg>
        <svg
          ref={artRef}
          className="flow-art"
          viewBox="0 0 840 440"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <g className="sphere">
            <circle cx="415" cy="220" r="180" />
            <ellipse cx="415" cy="220" rx="90" ry="180" />
            <ellipse cx="415" cy="220" rx="150" ry="180" />
            <ellipse cx="415" cy="220" rx="180" ry="55" />
            <ellipse cx="415" cy="220" rx="180" ry="115" />
            <path d="M235 220H595M415 40V400" />
          </g>
          <g ref={routesRef} className="routes" />
        </svg>
        <div className="products">
          <div className="products-emblem">
            <AppIcon name="package" />
          </div>
          <h3>Products</h3>
          {FLOW_CATEGORIES.map((category) => (
            <button
              key={category.name}
              type="button"
              className="product"
              data-product={category.name}
              data-cid-light=""
              aria-label={`Highlight ${category.label} matching`}
              onPointerEnter={() => setSelected(category.name)}
              onPointerLeave={() => setSelected(null)}
              onFocus={() => setSelected(category.name)}
              onBlur={() => setSelected(null)}
              onClick={() => setSelected(category.name)}
            >
              <AppIcon name={category.icon} />
              {category.label}
            </button>
          ))}
        </div>
        <div className="supplier partner" data-cid-light="">
          <div className="node-emblem">
            <AppIcon name="factory" />
          </div>
          <span>
            Qualified
            <br />
            suppliers
            <BuyerColors />
          </span>
        </div>
        <div className="ai-core">
          <div className="ai-emblem">AI</div>
          <span className="matching">Matching</span>
        </div>
        <div className="buyer partner" data-cid-light="">
          <div className="node-emblem">
            <AppIcon name="users" />
          </div>
          <span>
            Qualified buyers
            <br />
            globally
            <BuyerColors />
          </span>
        </div>
        <div className="trade-support partner" data-cid-light="">
          <div className="node-emblem">
            <AppIcon name="handshake" />
          </div>
          <span>
            Trade partners
            <small>
              <span className="trade-services">
                {TRADE_SERVICES.map((service) => (
                  <span key={service}>{service}</span>
                ))}
              </span>
            </small>
          </span>
        </div>
      </div>
    </div>
  );
}
